import { Slide } from '@mui/material'
import cx from 'classnames'
import React, { useContext, useEffect, useState } from 'react'

import { QuizContext } from '@f/applications/QuizApp/context/QuizContext'
import {
  composeTileOptions,
  RadioStepOption
} from '@f/applications/QuizApp/partials/composeTileOptions'
import QuestionHeading from '@f/applications/QuizApp/partials/QuestionHeading'
import { STEP_TRANSITION_DURATION } from '@f/applications/QuizApp/partials/quizConstants'
import { QuizStep } from '@f/applications/QuizApp/partials/types'
import { QuestionRadioButton } from '@f/applications/QuizApp/questions/QuestionRadio/QuestionRadioButton'
import { HouseSizeLIcon } from '@f/elements/Icon/HouseSizeLIcon'
import { HouseSizeMIcon } from '@f/elements/Icon/HouseSizeMIcon'
import { HouseSizeSIcon } from '@f/elements/Icon/HouseSizeSIcon'
import { HouseSizeXSIcon } from '@f/elements/Icon/HouseSizeXSIcon'

import { $TSFixMe } from '@f-fertighaus/applications/UserAccount/interfaces/utils'

interface Props {
  step: QuizStep
}

export const smallSizeOptions: RadioStepOption[] = [
  {
    value: 74,
    icon: 'small-64px',
    label: 'Bis 80 m²',
    inputProps: { dataset: { budgetCalcValue: '80' } }
  },
  {
    value: 80,
    icon: 'medium-64px',
    label: '80m² - 100m²',
    inputProps: { dataset: { budgetCalcValue: '100' } }
  },
  {
    value: 100,
    icon: 'large-64px',
    label: '100m² - 120m²',
    inputProps: { dataset: { budgetCalcValue: '120' } }
  }
]

const DEFAULT_ICON_NAMES = [
  'HouseSizeXSIcon',
  'HouseSizeSIcon',
  'HouseSizeMIcon',
  'HouseSizeLIcon'
] as const

export const baseSizeOptions: RadioStepOption[] = [
  {
    value: '<120',
    icon: HouseSizeXSIcon,
    label: 'Bis 120 m²'
  },
  {
    value: 120,
    icon: HouseSizeSIcon,
    label: '120m² - 140m²',
    inputProps: { dataset: { budgetCalcValue: '140' } }
  },
  {
    value: 140,
    icon: HouseSizeMIcon,
    label: '140m² - 160m²',
    inputProps: { dataset: { budgetCalcValue: '160' } }
  },
  {
    value: '>160',
    icon: HouseSizeLIcon,
    label: 'Über 160 m²'
  }
]

export const largeSizeOptions: RadioStepOption[] = [
  {
    value: 160,
    icon: 'small-64px',
    label: '160m² - 180m²',
    inputProps: { dataset: { budgetCalcValue: '180' } }
  },
  {
    value: 180,
    icon: 'medium-64px',
    label: '180m² - 240m²',
    inputProps: { dataset: { budgetCalcValue: '200' } }
  },
  {
    value: 250,
    icon: 'large-64px',
    label: '240m² - 300m²',
    inputProps: { dataset: { budgetCalcValue: '275' } }
  },
  {
    value: 300,
    icon: 'extra-large-64px',
    label: 'Über 300 m²',
    inputProps: { dataset: { budgetCalcValue: '300' } }
  }
]

export const QuestionLivingArea = ({ step }: Props) => {
  const {
    actions: { updateValues, move },
    tracking,
    tileStyle
  } = useContext(QuizContext) as $TSFixMe
  const [showSizeOptions, setShowSizeOptions] = useState<
    'base' | 'small' | 'large'
  >('base')
  const [showStep, setShowStep] = useState<boolean>(true)

  const composeTiles = composeTileOptions(tileStyle)

  const handleStepTransition = (stepName: 'base' | 'small' | 'large') => {
    setShowStep(false)
    setTimeout(() => {
      setShowSizeOptions(stepName)
      setShowStep(true)
    }, STEP_TRANSITION_DURATION)
  }

  useEffect(() => {
    function sendDisplayTrackingEvent(nameValue: string) {
      window._wt({
        category: 'question',
        action: 'display',
        values: [
          {
            type: 'name',
            value: nameValue
          }
        ],
        ...tracking.flow
      })
    }

    if (showSizeOptions === 'large') {
      sendDisplayTrackingEvent('living-area-large')
    }
    if (showSizeOptions === 'small') {
      sendDisplayTrackingEvent('living-area-small')
    }
  }, [showSizeOptions, tracking.flow])

  const handleLivingAreaChange = (value: number) => {
    if (!isNaN(value)) {
      updateValues({
        stepID: step.id,
        valuesToSave: [
          {
            name: 'living_area_total',
            value
          }
        ]
      })

      move({ to: 'next', triggerSubmit: true })
    }
  }
  const baseSizeOptionsWithOverrides = baseSizeOptions.map((option, index) => ({
    ...option,
    icon:
      !step.options?.[index].icon ||
      DEFAULT_ICON_NAMES.includes(step.options?.[index].icon)
        ? option.icon
        : step.options[index].icon,
    image: step.options?.[index].image
  }))

  const baseSizeOptionsWithOnClick: RadioStepOption[] = [
    {
      ...baseSizeOptionsWithOverrides[0],
      onClick: () => {
        handleStepTransition('small')
      }
    },
    baseSizeOptionsWithOverrides[1],
    baseSizeOptionsWithOverrides[2],
    {
      ...baseSizeOptionsWithOverrides[3],
      onClick: () => {
        handleStepTransition('large')
      }
    }
  ]

  const currentRenderOptions = {
    base: composeTiles(baseSizeOptionsWithOnClick),
    small: composeTiles(smallSizeOptions),
    large: composeTiles(largeSizeOptions)
  }[showSizeOptions]

  const composeTrackingLocation = (wtStepName: string) => {
    return {
      base: `${wtStepName}`,
      small: 'living-area-small',
      large: 'living-area-large'
    }[showSizeOptions]
  }

  const handleClick = value => {
    handleLivingAreaChange(parseInt(value))
  }

  return (
    <Slide in={showStep} timeout={STEP_TRANSITION_DURATION} appear={false}>
      <div>
        <QuestionHeading heading={step.heading} subheading={step.subheading} />
        <div
          className={cx('[ f-m-h-auto ] QuestionRadio-wrap', {
            'has-more-than-four-elements': currentRenderOptions.length > 4,
            'has-four-elements': currentRenderOptions.length === 4
          })}>
          <div className='[ f-d-flc ] QuestionRadio-options'>
            <div
              className={cx(
                tileStyle === 'card'
                  ? 'QuestionRadio-card-tiles'
                  : 'QuestionRadio-bar-tiles'
              )}>
              {currentRenderOptions.map(({ ItemTemplate, ...option }) => (
                <QuestionRadioButton
                  key={option.value}
                  wtData={{
                    location: `question-${composeTrackingLocation(
                      step._wtName
                    )}`,
                    ...tracking.flow
                  }}
                  onClick={
                    option.onClick
                      ? option.onClick
                      : () => handleClick(option.value)
                  }>
                  <ItemTemplate {...option} />
                </QuestionRadioButton>
              ))}
            </div>
          </div>
        </div>
      </div>
    </Slide>
  )
}
